import Modal from "@shared/components/base/Modal";
import Button from "@shared/components/base/Button";
import { THEME } from "@shared/constants/designSystem";
import { AlertTriangle } from "lucide-react";
import PropTypes from "prop-types";

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning", // warning, danger, info
  isLoading = false,
  children,
  autoClose = true, // Default true for backward compatibility
}) => {
  const getVariant = () => {
    switch (variant) {
      case "danger":
        return {
          icon: AlertTriangle,
          iconColor: "text-red-500",
          confirmVariant: "secondary",
          confirmClass: "bg-red-600 hover:bg-red-700 text-white",
        };
      case "info":
        return {
          icon: AlertTriangle,
          iconColor: "text-blue-500",
          confirmVariant: "primary",
          confirmClass: "",
        };
      default: // warning
        return {
          icon: AlertTriangle,
          iconColor: "text-yellow-500",
          confirmVariant: "secondary",
          confirmClass: "bg-yellow-600 hover:bg-yellow-700 text-white",
        };
    }
  };

  const config = getVariant();
  const Icon = config.icon;

  const handleConfirm = () => {
    onConfirm();
    if (!isLoading && autoClose) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={message}
    >
      <div className="space-y-4">
        {/* Icon and Message */}
        <div className="flex items-start gap-3">
          <Icon className={`w-6 h-6 ${config.iconColor} mt-0.5`} />
          <div className="flex-1">
            {children ? (
              children
            ) : (
              <p className={`${THEME.COLORS.TEXT.DEFAULT}`}>{message}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2"
          >
            {cancelText}
          </Button>
          <Button
            variant={config.confirmVariant}
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 ${config.confirmClass}`}
          >
            {isLoading ? "Processing..." : confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  variant: PropTypes.oneOf(["warning", "danger", "info"]),
  isLoading: PropTypes.bool,
  children: PropTypes.node,
  autoClose: PropTypes.bool,
};

export default ConfirmationModal;
export { ConfirmationModal };
